// ./src/utils/api/FormStatsService.ts
import { gql } from "@apollo/client";
import { ApiService } from "./ApiService";
import { OpResult } from "@sdflc/api-helpers";
import { Pagination, SortItem } from "./commonApiTypes";
import { Form } from "./FormService";

// GraphQL queries
const FORM_STATS_LIST_QUERY = gql`
  query FormStatsList($filter: FormStatsFilter, $params: PaginationAndSorting) {
    formStatsList(filter: $filter, params: $params) {
      code
      data {
        id
        accountId
        submissionsQty
        successfulSubmissionsQty
        failedSubmissionsQty
        lastSubmissionAt
        firstSubmissionAt
        filesSize
        filesQty
        createdAt
        updatedAt
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const FORM_STATS_GET_QUERY = gql`
  query FormStatsGet($id: ID) {
    formStatsGet(id: $id) {
      code
      data {
        id
        accountId
        submissionsQty
        successfulSubmissionsQty
        failedSubmissionsQty
        lastSubmissionAt
        firstSubmissionAt
        filesSize
        filesQty
        createdAt
        updatedAt
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const FORM_STATS_GET_MANY_QUERY = gql`
  query FormStatsGetMany($ids: [ID]) {
    formStatsGetMany(ids: $ids) {
      code
      data {
        id
        accountId
        submissionsQty
        successfulSubmissionsQty
        failedSubmissionsQty
        lastSubmissionAt
        firstSubmissionAt
        filesSize
        filesQty
        createdAt
        updatedAt
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

// Types
export interface FormStats {
  id?: string | undefined;
  accountId?: string | undefined;
  submissionsQty?: number | undefined;
  successfulSubmissionsQty?: number | undefined;
  failedSubmissionsQty?: number | undefined;
  lastSubmissionAt?: string | undefined;
  firstSubmissionAt?: string | undefined;
  filesSize?: string | undefined;
  filesQty?: string | undefined;
  createdAt?: string | undefined;
  updatedAt?: string | undefined;
  form?: Form;
}

export interface FormStatsFilter {
  id?: string[] | null | undefined;
  accountId?: string[] | null | undefined;
}

/**
 * FormStats service that handles form statistics-related API calls
 * Read-only operations: list, get, getMany (stats are managed internally by the system)
 */
export class FormStatsService {
  /**
   * Get a list of form stats with optional filtering, pagination, and sorting
   */
  static async list(
    filter?: FormStatsFilter,
    pagination?: Pagination,
    sortBy?: SortItem[]
  ): Promise<OpResult> {
    const result = await ApiService.executeQuery(FORM_STATS_LIST_QUERY, {
      filter: filter || {},
      params: {
        pagination: pagination || undefined,
        sortBy: sortBy || undefined,
      },
    });
    return result;
  }

  /**
   * Get form stats by form ID
   */
  static async get(id: string): Promise<OpResult> {
    const result = await ApiService.executeQuery(FORM_STATS_GET_QUERY, {
      id: id,
    });
    return result;
  }

  /**
   * Get multiple form stats by form IDs (preserves order)
   */
  static async getMany(ids: string[]): Promise<OpResult> {
    const result = await ApiService.executeQuery(FORM_STATS_GET_MANY_QUERY, {
      ids: ids,
    });
    return result;
  }
}
